import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiRefreshCw, FiSearch, FiShield, FiFilter } from 'react-icons/fi';
import { agencyAPI, superAdminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role_slug === 'super-admin' || currentUser?.role === 'super-admin';

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Filtres (super-admin uniquement)
  const [filterAgencyId, setFilterAgencyId] = useState('');
  const [search, setSearch] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '', role_id: '', agency_id: '', is_active: true
  });

  // Chargement des utilisateurs
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isSuperAdmin) {
        if (filterAgencyId) params.agencyId = filterAgencyId;
        if (search.trim()) params.search = search.trim();
      }
      const res = await agencyAPI.getUsers(params);
      setUsers(res.data || []);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erreur lors du chargement des utilisateurs.' });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, filterAgencyId, search]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await agencyAPI.getRoles();
      setRoles(res.data || []);
    } catch {}
  }, []);

  const fetchAgencies = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await superAdminAPI.getAgencies();
      setAgencies(res.data || []);
    } catch {}
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchRoles();
    fetchAgencies();
  }, [fetchRoles, fetchAgencies]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreate = () => {
    setEditUser(null);
    setForm({
      email: '', password: '', first_name: '', last_name: '',
      phone: '', role_id: '', agency_id: isSuperAdmin ? '' : (currentUser?.agencyId || ''), is_active: true
    });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role_id: user.role_id || '',
      agency_id: user.agency_id || '',
      is_active: user.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      if (editUser) {
        await agencyAPI.updateUser(editUser.id, {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          role_id: form.role_id,
          is_active: form.is_active,
        });
        setFeedback({ type: 'success', text: `Utilisateur ${form.first_name} ${form.last_name} mis a jour avec succes.` });
      } else {
        const payload = {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          role_id: form.role_id,
        };
        if (isSuperAdmin && form.agency_id) payload.agency_id = form.agency_id;
        await agencyAPI.createUser(payload);
        setFeedback({ type: 'success', text: `Utilisateur ${form.first_name} ${form.last_name} cree avec succes.` });
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erreur lors de l enregistrement.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer l utilisateur ${user.first_name} ${user.last_name} ?`)) return;
    try {
      await agencyAPI.deleteUser(user.id);
      setFeedback({ type: 'success', text: `Utilisateur ${user.first_name} ${user.last_name} supprime.` });
      fetchUsers();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la suppression.' });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await agencyAPI.updateUser(user.id, { is_active: !user.is_active });
      fetchUsers();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la mise a jour du statut.' });
    }
  };

  const getRoleBadgeColor = (slug) => {
    switch (slug) {
      case 'super-admin':    return { bg: '#fef9c3', color: '#854d0e', border: '#fde047' };
      case 'admin-agence':   return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
      case 'agent':          return { bg: '#dcfce7', color: '#166534', border: '#86efac' };
      case 'comptable':      return { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' };
      case 'locataire':      return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
      case 'client':         return { bg: '#fff7ed', color: '#9a3412', border: '#fdba74' };
      case 'prestataire':    return { bg: '#f0fdf4', color: '#14532d', border: '#86efac' };
      default:               return { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
    }
  };

  const columns = [
    {
      header: 'Utilisateur',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0
          }}>
            {(r.first_name?.[0] || '?').toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.first_name} {r.last_name}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{r.email}</div>
            {r.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>📞 {r.phone}</div>}
          </div>
        </div>
      )
    },
    {
      header: 'Rôle',
      render: r => {
        const style = getRoleBadgeColor(r.role_slug);
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
            background: style.bg, color: style.color, border: `1px solid ${style.border}`
          }}>
            {r.role_slug === 'super-admin' && '👑 '}
            {r.role_name || r.role_slug || '—'}
          </span>
        );
      }
    },
    // Colonne Agence visible uniquement pour le Super Admin
    ...(isSuperAdmin ? [{
      header: 'Agence',
      render: r => r.agency_name
        ? <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>🏢 {r.agency_name}</span>
        : <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>Aucune (Super Admin)</span>
    }] : []),
    {
      header: 'Statut',
      render: r => (
        <span style={{
          padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
          background: r.is_active ? '#dcfce7' : '#fee2e2',
          color: r.is_active ? '#166534' : '#991b1b'
        }}>
          {r.is_active ? '🟢 Actif' : '🔴 Inactif'}
        </span>
      )
    },
    {
      header: 'Dernière connexion',
      render: r => r.last_login
        ? new Date(r.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : <span style={{ color: '#94a3b8', fontSize: 12 }}>Jamais</span>
    },
    {
      header: 'Actions',
      render: r => {
        const isOwnAccount = r.id === currentUser?.id;
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className="btn btn-sm btn-secondary"
              title="Modifier"
              onClick={() => openEdit(r)}
              disabled={isOwnAccount && !isSuperAdmin}
            >
              <FiEdit2 />
            </button>
            <button
              className="btn btn-sm btn-secondary"
              title={r.is_active ? 'Desactiver' : 'Activer'}
              onClick={() => handleToggleActive(r)}
              disabled={isOwnAccount}
            >
              {r.is_active ? <FiToggleRight style={{ color: '#22c55e' }} /> : <FiToggleLeft style={{ color: '#94a3b8' }} />}
            </button>
            <button
              className="btn btn-sm btn-danger"
              title="Supprimer"
              onClick={() => handleDelete(r)}
              disabled={isOwnAccount}
            >
              <FiTrash2 />
            </button>
          </div>
        );
      }
    },
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            {isSuperAdmin && <FiShield style={{ color: '#7c3aed' }} />}
            {isSuperAdmin ? 'Gestion globale des utilisateurs' : 'Utilisateurs de votre agence'}
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            {isSuperAdmin
              ? 'Vision complète de tous les utilisateurs de la plateforme, toutes agences confondues.'
              : 'Gérez uniquement les membres et collaborateurs de votre agence.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchUsers}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
          <button className="btn btn-sm" style={{ background: '#7c3aed', color: '#fff', fontWeight: 700 }} onClick={openCreate}>
            <FiPlus style={{ marginRight: 6 }} /> Nouvel Utilisateur
          </button>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 8,
          borderLeft: '5px solid ' + (feedback.type === 'success' ? '#22c55e' : '#ef4444'),
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 600, color: feedback.type === 'success' ? '#15803d' : '#b91c1c' }}>{feedback.text}</span>
          <button className="btn btn-sm btn-secondary" onClick={() => setFeedback(null)}>X</button>
        </div>
      )}

      {/* KPI Band */}
      <div className="grid-4" style={{ marginBottom: 24, gap: 16 }}>
        <div className="card" style={{ padding: '14px 18px', background: '#f5f3ff' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#7c3aed' }}>Total Utilisateurs</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#7c3aed' }}>{users.length}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', background: '#dcfce7' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#166534' }}>Actifs</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#166534' }}>{users.filter(u => u.is_active).length}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', background: '#fee2e2' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#991b1b' }}>Inactifs</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#991b1b' }}>{users.filter(u => !u.is_active).length}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', background: isSuperAdmin ? '#fef9c3' : '#dbeafe' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: isSuperAdmin ? '#854d0e' : '#1e40af' }}>
            {isSuperAdmin ? 'Super Admins' : 'Admins Agence'}
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: isSuperAdmin ? '#854d0e' : '#1e40af' }}>
            {users.filter(u => isSuperAdmin ? u.role_slug === 'super-admin' : u.role_slug === 'admin-agence').length}
          </div>
        </div>
      </div>

      {/* Filtres Super Admin */}
      {isSuperAdmin && (
        <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <FiFilter style={{ color: '#7c3aed' }} />
            <div className="form-group" style={{ margin: 0, minWidth: 240 }}>
              <select
                className="form-control"
                value={filterAgencyId}
                onChange={e => setFilterAgencyId(e.target.value)}
                style={{ fontSize: 13 }}
              >
                <option value="">Toutes les agences</option>
                {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, position: 'relative', flex: 1, minWidth: 220 }}>
              <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher par nom, email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 13 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>
            {isSuperAdmin ? 'Tous les utilisateurs' : 'Utilisateurs de votre agence'} ({users.length})
          </h2>
        </div>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <DataTable
            columns={columns}
            data={users}
            emptyMessage={isSuperAdmin ? 'Aucun utilisateur trouvé sur la plateforme.' : 'Aucun utilisateur dans votre agence.'}
          />
        )}
      </div>

      {/* Bandeau securite pour les agences */}
      {!isSuperAdmin && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          🔒 <strong>Isolation agence</strong> : Vous ne pouvez gérer que les utilisateurs de votre propre agence. Le rôle <strong>Super Administrateur</strong> ne peut pas être assigné depuis cette interface.
        </div>
      )}

      {/* Modal creation / edition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUser ? `Modifier : ${editUser.first_name} ${editUser.last_name}` : 'Nouvel Utilisateur'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid-2" style={{ gap: 14 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Prenom *</label>
              <input className="form-control" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Nom *</label>
              <input className="form-control" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
            </div>
          </div>

          {!editUser && (
            <div style={{ marginTop: 14 }}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Email *</label>
                <input type="email" className="form-control" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Mot de passe *</label>
                <input type="password" className="form-control" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 caracteres" />
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 14, marginBottom: 14 }}>
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Telephone</label>
            <input className="form-control" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Ex: 70123456" />
          </div>

          <div className="form-group" style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>Role *</label>
            <select className="form-control" required value={form.role_id} onChange={e => setForm({ ...form, role_id: e.target.value })}>
              <option value="">Selectionner un role</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.slug === 'super-admin' ? '👑 ' : ''}
                  {r.name}
                </option>
              ))}
            </select>
            {!isSuperAdmin && (
              <small style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'block' }}>
                Le role Super Administrateur est reserve a la plateforme et n'est pas disponible ici.
              </small>
            )}
          </div>

          {/* Agence cible — Super Admin uniquement, en creation uniquement */}
          {isSuperAdmin && !editUser && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Agence d'appartenance <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optionnel pour le Super Admin)</span>
              </label>
              <select className="form-control" value={form.agency_id} onChange={e => setForm({ ...form, agency_id: e.target.value })}>
                <option value="">Aucune (Super Admin sans agence)</option>
                {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {editUser && (
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ width: 16, height: 16, accentColor: '#22c55e' }}
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Compte actif</span>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Annuler</button>
            <button type="submit" className="btn" disabled={saving} style={{ background: '#7c3aed', color: '#fff', fontWeight: 700 }}>
              {saving ? 'Enregistrement...' : editUser ? 'Mettre a jour' : 'Creer l utilisateur'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagementPage;
